// web/src/lib/mockAdmin.ts — mock data for the admin console (portal spec 22 §4d), so VITE_USE_MOCK=1 renders the board,
// client detail and every queue. One card in each of: demo (house org), demo (attached to a lead), sold (waiting on
// nameservers), sold (ready), going_live, live (run in review), live (site down), live (run held), live (nothing to do),
// paused (payment failed, 3 days), offboarding, legacy (2 fields missing), archived; two outbox drafts; one failed
// lifecycle task. Stateful within the page (a full reload starts over). The next-action rules mirror the API's
// nextAction() (api/src/modules/admin/admin-sites.service.ts) closely enough for a mock.
import type { Api } from './api';
import type {
  AdminAuditRow, AdminBillingCache, AdminCounts, AdminInvite, AdminLogin, AdminProposal, AdminReadyRow, AdminReport, AdminRequestRow,
  AdminSiteDetail, AdminSiteRow, BoardCard, BoardStage, BoardTone, LifecycleTask, MonitorStateDto, OnboardingDto, OutboxItem,
  RegistrySite, ReportDoc, SiteStage, UpdateRun, UpdateSpend,
} from './types';

export type ConsoleKey =
  | 'adminBoard' | 'adminCounts' | 'adminReady' | 'adminSites' | 'adminSite' | 'adminPatchSite' | 'adminMoveOver' | 'adminReleaseRequestLock'
  | 'adminFleet' | 'adminReport' | 'adminPublishReport' | 'adminUnpublishReport' | 'adminRemoveLogin' | 'adminInvites' | 'adminInvite'
  | 'adminRevokeInvite' | 'adminLifecycle' | 'adminCancelTask' | 'adminRetryLifecycleTask' | 'adminAttachTask' | 'adminSiteOff' | 'adminSiteOn'
  | 'adminOffboard' | 'adminOutbox' | 'adminEditOutbox' | 'adminApproveOutbox' | 'adminCancelOutbox' | 'adminRetryOutbox'
  | 'adminMonitorToggle' | 'adminMonitorCheck' | 'adminRunUpdate' | 'adminUpdateRuns' | 'adminUpdateDue' | 'adminUpdateSpend'
  | 'adminReleaseRun' | 'adminRetryRun' | 'adminCancelRun' | 'adminProposalsReview' | 'adminProposal' | 'adminDecideOnBehalf'
  | 'adminCreateRequest' | 'adminUploadSas' | 'adminReleasePublishLock';
type Prev = Pick<Api, 'adminOnboarding' | 'adminStartGoLive' | 'adminLeads'>;

const DAY = 86_400_000;
const delay = <T>(v: T, ms = 220): Promise<T> => new Promise((r) => setTimeout(() => r(v), ms));
const err = (statusCode: number, message: string) => Promise.reject(new Error(JSON.stringify({ error: { message, statusCode } })));
const at = (daysAgo: number) => new Date(Date.now() - daysAgo * DAY).toISOString();
const ym = (d = new Date()) => d.toISOString().slice(0, 7);
let seq = 500;
const uid = () => `00000000-0000-4000-8000-${String(++seq).padStart(12, '0')}`;

const HOUSE = 'org_house';
const ORGS: Record<string, string> = {
  [HOUSE]: 'YourProject.ai (house)', org_pat: 'Pat’s Roofing', org_lake: 'Lakeside Dental', org_canyon: 'Canyon HVAC', org_blue: 'Blue Door Cafe',
  org_summit: 'Summit Plumbing', org_granite: 'Granite Tile', org_maple: 'Maple Family Law', org_cedar: 'Cedar Dental',
  org_oak: 'Oak Street Yoga', org_river: 'River Bend Auto', org_mill: 'Old Mill Inn',
};

function site(slug: string, name: string, orgId: string, stage: SiteStage, daysInStage: number, over: Partial<RegistrySite> = {}): RegistrySite {
  const live = ['live', 'paused', 'offboarding', 'legacy'].includes(stage);
  return {
    id: `site_${slug}`, orgId, slug, name, url: null, stage, stageChangedAt: at(daysInStage),
    domain: live ? `${slug}.test` : null, liveHost: live ? `${slug}.test` : null, liveAt: live ? at(daysInStage + 60) : null,
    previewUrl: `https://main.d${slug.length}abc.amplifyapp.com`, takesPayments: false,
    repoUrl: `https://github.com/kc-hansen/${slug}`, repoOwner: 'kc-hansen', repoName: slug, amplifyAppId: live ? `d${slug.length}abc` : null,
    awsRegion: live ? 'us-east-1' : null, cfZoneId: null, zoneStatus: null, gscProperty: live ? `sc-domain:${slug}.test` : null,
    runDay: live ? 12 : null, nextRunAt: live ? new Date(Date.now() + 17 * DAY).toISOString() : null, lastRunAt: null, lastRunResult: null,
    costCapUsd: null, kcReviewRunsLeft: 3, reportEmails: live ? [`owner@${slug}.test`] : [], updateMode: 'show_me', gbpManaged: live ? false : null,
    planInterval: live ? 'month' : null, pausedReason: null, siteOffAt: null, goliveStartedAt: null, showcase: false,
    busyRef: null, busySince: null, publishRef: null, publishSince: null, createdAt: at(daysInStage + 90), ...over,
  };
}

const sites: RegistrySite[] = [
  site('sunrise-bakery-demo', 'Sunrise Bakery (demo)', HOUSE, 'demo', 9, { repoUrl: null }),
  site('pats-roofing-demo', 'Pat’s Roofing (demo)', 'org_pat', 'demo', 4),
  site('lakeside-dental', 'Lakeside Dental', 'org_lake', 'sold', 6, { cfZoneId: 'zone_lake', zoneStatus: 'pending', takesPayments: true }),
  site('canyon-hvac', 'Canyon HVAC', 'org_canyon', 'sold', 3, { cfZoneId: 'zone_canyon', zoneStatus: 'active', planInterval: 'year' }),
  site('blue-door-cafe', 'Blue Door Cafe', 'org_blue', 'going_live', 1, { goliveStartedAt: at(1), cfZoneId: 'zone_blue', zoneStatus: 'active', takesPayments: true }),
  site('summit-plumbing', 'Summit Plumbing', 'org_summit', 'live', 40, { lastRunAt: at(33), lastRunResult: 'ok', cfZoneId: 'zone_summit' }),
  site('granite-tile', 'Granite Tile', 'org_granite', 'live', 70, { lastRunAt: at(20), lastRunResult: 'ok', takesPayments: true, cfZoneId: 'zone_granite' }),
  site('maple-family-law', 'Maple Family Law', 'org_maple', 'live', 120, { lastRunAt: at(2), lastRunResult: 'held', kcReviewRunsLeft: 1, cfZoneId: 'zone_maple' }),
  site('cedar-dental', 'Cedar Dental', 'org_cedar', 'live', 200, { lastRunAt: at(12), lastRunResult: 'ok', kcReviewRunsLeft: 0, updateMode: 'auto', planInterval: 'year', cfZoneId: 'zone_cedar' }),
  site('oak-street-yoga', 'Oak Street Yoga', 'org_oak', 'paused', 3, { pausedReason: 'payment_failed', siteOffAt: new Date(Date.now() + 4 * DAY).toISOString(), cfZoneId: 'zone_oak' }),
  site('river-bend-auto', 'River Bend Auto', 'org_river', 'offboarding', 5, { cfZoneId: 'zone_river' }),
  site('old-mill-inn', 'Old Mill Inn', 'org_mill', 'legacy', 400, { gscProperty: null, cfZoneId: null, nextRunAt: null, runDay: 6 }),
  site('harbor-books-demo', 'Harbor Books (demo)', HOUSE, 'archived', 75),
];
const bySlug = (slug: string) => sites.find((s) => s.slug === slug);

const bill: Record<string, AdminBillingCache> = {};
for (const s of sites) {
  if (s.orgId === HOUSE || s.stage === 'demo' || s.stage === 'archived') continue;
  const status = s.stage === 'paused' ? 'past_due' : s.stage === 'sold' && s.slug === 'lakeside-dental' ? 'trialing'
    : s.stage === 'sold' || s.stage === 'going_live' ? 'trialing' : s.stage === 'offboarding' ? 'active' : s.slug === 'old-mill-inn' ? 'active' : 'active';
  bill[s.orgId] = { orgId: s.orgId, stripeCustomerId: `cus_mock_${s.slug.slice(0, 6)}`, subStatus: status, currentPeriodEnd: Math.floor((Date.now() + 12 * DAY) / 1000),
    cancelAtPeriodEnd: s.stage === 'offboarding', chargesEnabled: s.slug === 'granite-tile', payoutsEnabled: s.slug === 'granite-tile',
    connectedAccountId: s.takesPayments ? `acct_mock_${s.slug.slice(0, 5)}` : null, commissionMonthCents: s.slug === 'granite-tile' ? 175 : 0, incomeMonthCents: s.slug === 'granite-tile' ? 42_000 : 0 };
}

const mon: Record<string, MonitorStateDto> = {};
const monFor = (s: RegistrySite, status: MonitorStateDto['status'], enabled = true, sinceDays = 9): MonitorStateDto => ({
  siteId: s.id, enabled, host: s.liveHost ?? '', status, since: at(sinceDays), lastOkAt: status === 'up' ? at(0.003) : at(0.2),
  lastCheckAt: at(0.003), failStreak: status === 'down' ? 4 : 0, certExpiresAt: new Date(Date.now() + 61 * DAY).toISOString(), certCheckedAt: at(0.4), certError: null,
});
for (const s of sites) if (['live', 'paused', 'offboarding', 'legacy'].includes(s.stage)) mon[s.id] = monFor(s, 'up');
mon['site_granite-tile'] = monFor(bySlug('granite-tile')!, 'down', true, 0.08);

const ticks: Record<string, Omit<OnboardingDto, 'siteId' | 'slug' | 'stage' | 'checkedAt' | 'clientDone' | 'ready' | 'paymentsReady'>> = {
  'lakeside-dental': { account: 'done', plan: 'done', stripe: 'todo', preferences: 'todo', nameservers: 'todo',
    nameserverInfo: { nameServers: ['ana.ns.cloudflare.com', 'bob.ns.cloudflare.com'], registrar: 'GoDaddy', currentNameServers: ['ns51.domaincontrol.com', 'ns52.domaincontrol.com'] } },
  'canyon-hvac': { account: 'done', plan: 'done', stripe: 'not_needed', preferences: 'done', nameservers: 'done', nameserverInfo: null },
  'blue-door-cafe': { account: 'done', plan: 'done', stripe: 'done', preferences: 'done', nameservers: 'done', nameserverInfo: null },
};
const nsRechecks: Record<string, number> = {};
function onboarding(s: RegistrySite, refresh = false): OnboardingDto {
  const t = ticks[s.slug] ?? { account: s.orgId === HOUSE ? 'todo' : 'done', plan: 'todo', stripe: s.takesPayments ? 'todo' : 'not_needed', preferences: 'todo', nameservers: s.cfZoneId ? 'todo' : 'not_needed', nameserverInfo: null };
  if (refresh && t.nameservers === 'todo' && (nsRechecks[s.slug] = (nsRechecks[s.slug] ?? 0) + 1) >= 2) { t.nameservers = 'done'; s.zoneStatus = 'active'; }
  const ready = [t.account, t.plan, t.nameservers].every((x) => x !== 'todo');
  return { ...t, siteId: s.id, slug: s.slug, stage: s.stage, ready, clientDone: [t.account, t.plan, t.stripe].every((x) => x !== 'todo'),
    paymentsReady: t.stripe !== 'todo', checkedAt: new Date().toISOString() };
}

const task = (siteSlug: string | null, kind: LifecycleTask['kind'], status: LifecycleTask['status'], daysAgo: number, payload: Record<string, unknown> = {}): LifecycleTask => {
  const s = siteSlug ? bySlug(siteSlug) : undefined;
  return { id: uid(), siteId: s?.id ?? null, kind, status, payload, createdBy: kind === 'start_golive' ? 'admin:kc@yourproject.ai' : 'stripe',
    createdAt: at(daysAgo), doneAt: ['done', 'canceled', 'failed'].includes(status) ? at(Math.max(0, daysAgo - 0.1)) : null, slug: s?.slug ?? null, siteName: s?.name ?? null };
};
const tasks: LifecycleTask[] = [
  task('oak-street-yoga', 'site_off', 'failed', 0.5, { reason: 'payment', error: 'cloudflare error: 403 the token cannot edit redirect rules' }),
  task('oak-street-yoga', 'payment_failed', 'open', 3, { invoiceId: 'in_mock_1' }),
  task('blue-door-cafe', 'start_golive', 'running', 1, { slug: 'blue-door-cafe', forced: false }),
  task(null, 'prepare', 'open', 2, { email: 'jo@newclient.test', businessName: 'Jo’s Landscaping', unmatched: true, amountCents: 500_000 }),
  task('granite-tile', 'start_golive', 'done', 5, { slug: 'granite-tile' }),
];

const msg = (siteSlug: string, template: string, status: OutboxItem['status'], daysAgo: number, subject: string, body: string, error: string | null = null): OutboxItem => {
  const s = bySlug(siteSlug)!;
  return { id: uid(), siteId: s.id, orgId: s.orgId, toEmails: [`owner@${s.slug}.test`], template, vars: { siteName: s.name }, subject, body, status,
    needsReview: true, createdBy: template === 'welcome' ? 'robot:golive' : 'robot:update', sentAt: status === 'sent' ? at(daysAgo) : null, createdAt: at(daysAgo), error };
};
const outbox: OutboxItem[] = [
  msg('lakeside-dental', 'welcome', 'draft', 0.2, 'Welcome to your YourProject.ai dashboard',
    'Hi there,\n\nYour new website is almost ready. We need three things from you:\n1. Create your account: https://app.yourproject.ai/invite/••••\n2. Set up your plan\n3. Set up payments\n\nOnce we have them, you’re live.\n\nKC'),
  msg('summit-plumbing', 'monthly_note', 'draft', 0.1, 'Your October website update',
    'Hi,\n\nYour October report is ready: https://app.yourproject.ai/reports?site=site_summit-plumbing\n\nWe found two small things to improve. Have a look when you have a minute.\n\nKC'),
  msg('cedar-dental', 'monthly_note', 'sent', 2, 'Your October website update', 'Hi,\n\nYour October report is ready.\n\nKC'),
  msg('granite-tile', 'back_live', 'failed', 1, 'Your website is back', 'Hi,\n\nYour website is back on.\n\nKC', 'ACS_EMAIL_FROM not set'),
];

const reportDoc = (headline: string): ReportDoc => ({
  headline, practiceData: false, screenshots: [], done: ['Fixed a slow photo on the home page', 'Added your new service area'], noticed: ['More people found you through maps this month'],
  cards: [
    { key: 'customers', title: 'Your customers', lines: [{ label: 'Calls from your site', value: '38', delta: 'up', deltaText: '+6 vs last month' }, { label: 'Quote requests', value: '12', delta: 'flat' }] },
    { key: 'search', title: 'Finding you', lines: [{ label: 'Times you showed up in search', value: '4,210', delta: 'up', deltaText: '+12%' }] },
    { key: 'safe', title: 'Safe and sound', lines: [{ label: 'Up and running', value: '99.98%' }, { label: 'Security certificate', value: 'Good, renews in 61 days' }] },
  ],
});
const reports: AdminReport[] = [
  { id: uid(), siteId: 'site_summit-plumbing', slug: 'summit-plumbing', siteName: 'Summit Plumbing', period: ym(), windowFrom: at(30).slice(0, 10), windowTo: at(1).slice(0, 10),
    status: 'draft', publishedAt: null, emailedAt: null, updateRunId: null, doc: reportDoc('A busy month: more calls and two fixes'), createdBy: 'robot:update', createdAt: at(0.3), reportEmails: ['owner@summit-plumbing.test'] },
  { id: uid(), siteId: 'site_summit-plumbing', slug: 'summit-plumbing', siteName: 'Summit Plumbing', period: ym(new Date(Date.now() - 31 * DAY)), windowFrom: at(61).slice(0, 10), windowTo: at(31).slice(0, 10),
    status: 'published', publishedAt: at(30), emailedAt: at(30), updateRunId: null, doc: reportDoc('Steady month'), createdBy: 'robot:update', createdAt: at(31), reportEmails: ['owner@summit-plumbing.test'] },
];
const proposals: AdminProposal[] = [{
  id: uid(), siteId: 'site_summit-plumbing', period: ym(), previewUrl: 'https://update.d15abc.amplifyapp.com', status: 'review', updateRunId: null,
  createdAt: at(0.3), releasedAt: null, decidedAt: null, liveAt: null, autoYesOn: null,
  items: [
    { id: uid(), key: 'img-hero-size', class: 'green', title: 'Made the home page photo load faster', why: 'It was the slowest thing on the page.', source: null, screenshotUrl: null, commitShas: ['a1b2c3d'], status: 'applied', decidedAt: null, decidedBy: null, note: null },
    { id: uid(), key: 'service-area', class: 'green', title: 'Added Draper to your service area', why: 'People there searched for you.', source: null, screenshotUrl: null, commitShas: ['b2c3d4e'], status: 'applied', decidedAt: null, decidedBy: null, note: null },
    { id: uid(), key: 'reviews-block', class: 'yellow', title: 'Show your three newest reviews on the home page', why: 'Visitors trust reviews they can see right away.', source: null, screenshotUrl: null, commitShas: ['c3d4e5f', 'd4e5f6a'], status: 'pending', decidedAt: null, decidedBy: null, note: null },
    { id: uid(), key: 'call-button', class: 'yellow', title: 'A bigger “Call now” button on phones', why: 'Most of your visitors are on a phone.', source: null, screenshotUrl: null, commitShas: ['e5f6a7b'], status: 'pending', decidedAt: null, decidedBy: null, note: null },
    { id: uid(), key: 'financing', class: 'red', title: 'Offer financing on bigger jobs', why: 'Worth a chat: it changes how you sell.', source: null, screenshotUrl: null, commitShas: [], status: 'discuss', decidedAt: null, decidedBy: null, note: null },
  ],
}];
const run = (siteSlug: string, status: UpdateRun['status'], daysAgo: number, over: Partial<UpdateRun> = {}): UpdateRun => {
  const s = bySlug(siteSlug)!;
  return { id: uid(), slug: s.slug, siteName: s.name, siteId: s.id, period: ym(new Date(Date.now() - daysAgo * DAY)), reason: 'monthly', requestId: null, postReport: true,
    status, phase: 'monthly', phaseAttempts: { monthly: 1, request: 0, finalize: 0, revert: 0 }, costUsd: '3.40', costCapUsd: '10.00', proposalId: null, reportId: null,
    mergeSha: null, revertSha: null, publishReadyAt: null, usedPhotoIds: [], hasFindings: true, message: null, error: null, requestedBy: 'dispatcher',
    startedAt: at(daysAgo), finishedAt: ['live', 'held', 'failed', 'skipped'].includes(status) ? at(Math.max(0, daysAgo - 0.05)) : null,
    createdAt: at(daysAgo), updatedAt: at(Math.max(0, daysAgo - 0.05)), ...over };
};
const runs: UpdateRun[] = [
  run('summit-plumbing', 'review', 0.3, { costUsd: '4.12', reportId: reports[0]!.id, proposalId: proposals[0]!.id, message: 'report + 5 suggestions posted' }),
  run('maple-family-law', 'held', 2, { costUsd: '10.02', error: 'cost cap reached ($10.02 of $10.00)', message: 'held' }),
  run('maple-family-law', 'live', 9, { reason: 'request', phase: 'request', postReport: false, requestId: uid(), requestedBy: 'request:mock', costUsd: '0.84', message: 'request live' }),
  run('cedar-dental', 'live', 12, { costUsd: '2.95', message: 'live' }),
  run('summit-plumbing', 'live', 33, { costUsd: '3.80' }),
];
proposals[0]!.updateRunId = runs[0]!.id;
reports[0]!.updateRunId = runs[0]!.id;

const invites: Record<string, AdminInvite[]> = {
  'lakeside-dental': [{ id: uid(), email: 'office@lakeside-dental.test', businessName: 'Lakeside Dental', createdBy: 'robot:golive', createdAt: at(6), expiresAt: at(-8), acceptedAt: at(5), revokedAt: null }],
};
const users: Record<string, AdminLogin[]> = {};
for (const o of Object.keys(ORGS)) if (o !== HOUSE) users[o] = [{ id: uid(), email: `owner@${o.slice(4)}.test`, role: 'owner', lastLogin: at(1), createdAt: at(60) }];
users.org_summit!.push({ id: uid(), email: 'frontdesk@summit.test', role: 'member', lastLogin: null, createdAt: at(20) });
const requests: Record<string, AdminRequestRow[]> = {
  'maple-family-law': [{ id: uid(), title: 'Add our new office hours', status: 'live', kind: 'change', source: 'dashboard', createdBy: 'owner@maple.test', createdAt: at(10), updatedAt: at(9) }],
};
const audit: Record<string, AdminAuditRow[]> = {};
const log = (s: RegistrySite, action: string, meta: unknown = {}) => {
  (audit[s.id] ??= []).unshift({ id: uid(), actor: 'admin:kc@yourproject.ai', action, resource: s.id, meta, createdAt: new Date().toISOString() });
};
for (const s of sites) audit[s.id] = [{ id: uid(), actor: 'robot:golive', action: 'site.create', resource: s.id, meta: { slug: s.slug }, createdAt: s.createdAt }];

// ── the card (mirrors the API's nextAction) ──
const PRE_LIVE: SiteStage[] = ['sold', 'finishing', 'going_live'];
const LIVE_TYPE: SiteStage[] = ['live', 'paused', 'offboarding', 'legacy'];
const BOARD_ORDER: SiteStage[] = ['demo', 'sold', 'finishing', 'going_live', 'live', 'paused', 'offboarding', 'legacy', 'archived', 'offboarded'];
const days = (iso: string | null) => (iso ? Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / DAY)) : 0);
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const MOVE_FIELDS = ['repoUrl', 'liveHost', 'domain', 'amplifyAppId', 'awsRegion', 'cfZoneId', 'gscProperty', 'liveAt', 'runDay', 'reportEmails', 'updateMode'] as const;
function gaps(s: RegistrySite): string[] {
  const missing: string[] = MOVE_FIELDS.filter((k) => { const v = s[k]; return v === null || v === '' || (Array.isArray(v) && !v.length); });
  if (!['active', 'trialing'].includes(bill[s.orgId]?.subStatus ?? 'none')) missing.push('planActive');
  if (!mon[s.id]?.enabled) missing.push('monitoring');
  return missing;
}
const leadEmail: Record<string, string> = { 'pats-roofing-demo': 'pat@patsroofing.test' };

function next(s: RegistrySite, t: OnboardingDto | null): { label: string; tone: BoardTone } | null {
  const siteTasks = tasks.filter((x) => x.siteId === s.id && ['open', 'running', 'failed'].includes(x.status));
  const review = runs.some((r) => r.siteId === s.id && r.status === 'review');
  const drafts = outbox.filter((m) => m.siteId === s.id && m.status === 'draft' && m.needsReview).length;
  const m = mon[s.id];
  switch (s.stage) {
    case 'demo':
      if (s.showcase) return { label: 'Showcase (kept in your gallery)', tone: 'ok' };
      if (s.orgId === HOUSE) return { label: 'Attach to a lead, or mark showcase', tone: 'do' };
      return { label: `Follow up with ${leadEmail[s.slug] ?? 'the prospect'} (${plural(days(s.stageChangedAt), 'day')})`, tone: 'do' };
    case 'sold': case 'finishing': {
      const prep = siteTasks.find((x) => x.kind === 'prepare');
      if (prep) return { label: `Prepare: ${prep.status}`, tone: prep.status === 'failed' ? 'bad' : 'wait' };
      const waiting = t ? (['account', 'plan'] as const).filter((k) => t[k] === 'todo') : [];
      if (waiting.length) return { label: `Waiting on client: ${waiting.join(', ')}`, tone: 'wait' };
      if (t?.nameservers === 'todo') return { label: `Call ${s.name} to change nameservers`, tone: 'do' };
      return s.stage === 'sold' ? { label: 'Press Start go-live', tone: 'do' } : { label: 'Finish the build, then Start go-live', tone: 'do' };
    }
    case 'going_live':
      if (siteTasks.some((x) => x.kind === 'start_golive' && x.status === 'failed')) return { label: 'Go-live failed: open the task', tone: 'bad' };
      return { label: `Go-live running (${plural(days(s.goliveStartedAt), 'day')})`, tone: 'wait' };
    case 'live': case 'legacy':
      if (review) return { label: "Release this month's report", tone: 'do' };
      if (s.lastRunResult === 'held' || s.lastRunResult === 'failed') return { label: `Run ${s.lastRunResult}: retry or cancel`, tone: 'bad' };
      if (m?.enabled && m.status === 'down') return { label: `Site down since ${m.since.slice(0, 16).replace('T', ' ')} UTC`, tone: 'bad' };
      if (drafts) return { label: `Approve ${plural(drafts, 'message')}`, tone: 'do' };
      if (s.stage === 'legacy') { const n = gaps(s).length; return n ? { label: `Move over: ${plural(n, 'field')} to fill`, tone: 'do' } : { label: 'Move over: everything is filled, press Move to live', tone: 'do' }; }
      return { label: 'Nothing to do', tone: 'ok' };
    case 'paused': {
      if (s.pausedReason !== 'payment_failed') return { label: `Paused (${s.pausedReason ?? 'admin'})`, tone: 'wait' };
      const off = tasks.some((x) => x.siteId === s.id && x.kind === 'site_off' && x.status === 'done' && x.payload.offAt && !x.payload.onAt);
      const left = s.siteOffAt ? Math.ceil((Date.parse(s.siteOffAt) - Date.now()) / DAY) : 7 - days(s.stageChangedAt);
      const ago = days(s.stageChangedAt);
      return { label: `Payment failed ${ago ? `${plural(ago, 'day')} ago` : 'today'}: follow up (${off ? 'site is off' : left > 0 ? `site goes off in ${plural(left, 'day')}` : 'site goes off today'})`, tone: 'bad' };
    }
    case 'offboarding': {
      const end = bill[s.orgId]?.currentPeriodEnd;
      return { label: `Ends ${end ? new Date(end * 1000).toISOString().slice(0, 10) : 'at period end'}`, tone: 'wait' };
    }
    default: return null;
  }
}
function card(s: RegistrySite): BoardCard {
  const t = PRE_LIVE.includes(s.stage) ? onboarding(s) : null;
  const m = mon[s.id];
  const b = bill[s.orgId];
  const flags: string[] = [];
  if (s.stage === 'going_live' && days(s.goliveStartedAt) > 2) flags.push(`going live for ${days(s.goliveStartedAt)} days`);
  if (LIVE_TYPE.includes(s.stage) && (s.lastRunResult === 'held' || s.lastRunResult === 'failed')) flags.push(`last run ${s.lastRunResult}`);
  if (s.stage === 'paused') flags.push(s.pausedReason === 'payment_failed' ? 'payment failed' : 'paused');
  if (m?.enabled && m.status === 'down') flags.push('site down');
  if (tasks.some((x) => x.siteId === s.id && x.status === 'failed')) flags.push('lifecycle task failed');
  if ((s.stage === 'sold' || s.stage === 'finishing') && days(s.stageChangedAt) > 14 && t && (t.account === 'todo' || t.plan === 'todo')) flags.push('waiting on client 14+ days');
  return {
    slug: s.slug, siteId: s.id, name: s.name, org: ORGS[s.orgId] ?? '', stage: s.stage, stageDays: days(s.stageChangedAt), liveHost: s.liveHost, previewUrl: s.previewUrl,
    showcase: s.showcase, next: next(s, t), plan: { status: b?.subStatus ?? 'none', interval: s.planInterval },
    nameservers: t && t.nameservers !== 'not_needed' ? (t.nameservers === 'done' ? 'done' : 'not_yet') : null,
    payments: s.takesPayments ? (t ? (t.stripe === 'done' ? 'ready' : 'not_ready') : b?.chargesEnabled ? 'ready' : 'not_ready') : null,
    uptime: LIVE_TYPE.includes(s.stage) ? (m?.enabled ? m.status : 'off') : null,
    lastRunAt: s.lastRunAt, lastRunResult: s.lastRunResult, nextRunAt: s.nextRunAt, flags,
  };
}
const sortCards = (a: BoardCard, b: BoardCard) => b.flags.length - a.flags.length || b.stageDays - a.stageDays || a.name.localeCompare(b.name);
const mustSite = (slug: string) => bySlug(slug);
const notFound = (slug: string) => err(404, `site '${slug}' not found`);
const move = (s: RegistrySite, stage: SiteStage) => { s.stage = stage; s.stageChangedAt = new Date().toISOString(); };

export function adminConsoleMock(prev: Prev): Pick<Api, ConsoleKey | 'adminOnboarding' | 'adminStartGoLive'> {
  return {
    adminOnboarding: (slug, refresh) => { const s = mustSite(slug); return s ? delay(onboarding(s, !!refresh)) : prev.adminOnboarding(slug, refresh); },
    adminStartGoLive: (slug, force) => {
      const s = mustSite(slug);
      if (!s) return prev.adminStartGoLive(slug, force);
      if (s.stage !== 'sold' && s.stage !== 'finishing') return err(409, `site is ${s.stage}; Start go-live needs sold or finishing`);
      const o = onboarding(s);
      if (!o.ready && !force) return err(409, `not ready (still to do: ${(['account', 'plan', 'nameservers'] as const).filter((k) => o[k] === 'todo').join(', ')})`);
      move(s, 'going_live'); s.goliveStartedAt = new Date().toISOString();
      const t = task(s.slug, 'start_golive', 'open', 0, { slug: s.slug, forced: !!force && !o.ready });
      tasks.unshift(t); log(s, 'golive.start', { taskId: t.id });
      return delay({ taskId: t.id, stage: 'going_live' as const });
    },
    adminBoard: () => delay<BoardStage[]>(BOARD_ORDER.map((stage) => ({
      stage, collapsed: stage === 'archived' || stage === 'offboarded', cards: sites.filter((s) => s.stage === stage).map(card).sort(sortCards),
    }))),
    adminCounts: async () => {
      const leads = (await prev.adminLeads('new')).length;
      const ready = sites.filter((s) => (s.stage === 'sold' || s.stage === 'finishing') && onboarding(s).ready).length;
      return delay<AdminCounts>({
        leads, outbox: outbox.filter((m) => m.status === 'draft' && m.needsReview).length, review: runs.filter((r) => r.status === 'review').length,
        lifecycle: tasks.filter((t) => t.status === 'failed').length + sites.filter((s) => s.stage === 'paused' && s.pausedReason === 'payment_failed').length + ready,
      }, 120);
    },
    adminReady: () => delay<AdminReadyRow[]>(sites.filter((s) => s.stage === 'sold' || s.stage === 'finishing').map((s) => ({ s, t: onboarding(s) }))
      .filter(({ t }) => t.account === 'done' && t.plan === 'done').map(({ s, t }) => ({ slug: s.slug, name: s.name, stage: s.stage, nameservers: t.nameservers !== 'todo', ticks: t }))),
    adminSites: () => delay<AdminSiteRow[]>(sites.map((s) => ({ id: s.id, slug: s.slug, name: s.name, stage: s.stage, orgId: s.orgId, org: ORGS[s.orgId] ?? '',
      liveHost: s.liveHost, kcReviewRunsLeft: s.kcReviewRunsLeft, stageChangedAt: s.stageChangedAt }))),
    adminSite: (slug) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      const lp = proposals.filter((p) => p.siteId === s.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null;
      const detail: AdminSiteDetail = {
        site: { ...s }, org: { id: s.orgId, name: ORGS[s.orgId] ?? '', slug: s.orgId, stripeCustomerId: bill[s.orgId]?.stripeCustomerId ?? null, connectedAccountId: bill[s.orgId]?.connectedAccountId ?? null },
        isHouseOrg: s.orgId === HOUSE, card: card(s), onboarding: PRE_LIVE.includes(s.stage) ? onboarding(s) : null, billing: bill[s.orgId] ?? null,
        monitor: mon[s.id] ?? null,
        lead: s.slug === 'pats-roofing-demo' ? { id: 'lead_pat', email: 'pat@patsroofing.test', businessName: 'Pat’s Roofing', phone: '(801) 555-0144', currentSiteUrl: 'https://patsroofing.example',
          socials: [], biggestIssue: 'Nobody finds us on Google.', status: 'demo_ready', createdAt: at(12), orgId: s.orgId, siteId: s.id } : null,
        moveOver: s.stage === 'legacy' ? { missing: gaps(s) } : null,
        switchedOff: null,
        runs: runs.filter((r) => r.siteId === s.id), reports: reports.filter((r) => r.siteId === s.id), proposal: lp,
        tasks: tasks.filter((t) => t.siteId === s.id), messages: outbox.filter((m) => m.siteId === s.id), users: users[s.orgId] ?? [],
        requests: requests[s.slug] ?? [], audit: audit[s.id] ?? [],
      };
      return delay(structuredClone(detail));
    },
    adminPatchSite: (slug, p, force) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      if ('siteOffAt' in p) return err(400, 'set siteOffAt with Turn site off / on, not by hand');
      if (p.stage && p.stage !== s.stage && !force) return err(400, `stage ${s.stage} → ${String(p.stage)} not allowed`);
      if (p.stage === 'paused' && !p.pausedReason && !s.pausedReason) return err(400, 'pausedReason is required to pause a site');
      const was = s.stage;
      Object.assign(s, p);
      if (p.stage && p.stage !== was) { s.stageChangedAt = new Date().toISOString(); if (p.stage === 'live' && was === 'paused') s.pausedReason = null; }
      log(s, 'site.patch', { fields: Object.keys(p), force: !!force });
      return delay({ ...s });
    },
    adminMoveOver: (slug, p) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      if (s.stage !== 'legacy') return err(400, 'only legacy sites can be moved over');
      const missing = gaps({ ...s, ...(p as Partial<RegistrySite>) }).filter((g) => g !== 'monitoring');
      if (missing.length) return err(400, `still missing: ${missing.join(', ')}`);
      Object.assign(s, p);
      move(s, 'live');
      s.nextRunAt = new Date(Date.now() + 24 * DAY).toISOString();
      mon[s.id] = { ...(mon[s.id] ?? monFor(s, 'unknown')), enabled: true, host: s.liveHost ?? '' };
      log(s, 'site.patch', { fields: [...Object.keys(p), 'stage'] });
      return delay({ ...s });
    },
    adminReleaseRequestLock: (slug) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      const holder = s.busyRef; s.busyRef = null; s.busySince = null;
      if (holder) log(s, 'site.lock.admin_release', { ref: holder });
      return delay({ released: !!holder, holder });
    },
    adminFleet: () => delay({ _about: 'Export of the ypdash site registry (portal spec 22). Read-only; the registry is the source of truth.', generatedAt: new Date().toISOString(),
      sites: sites.filter((s) => ['live', 'going_live', 'paused', 'offboarding', 'legacy'].includes(s.stage)).map((s) => ({ slug: s.slug, name: s.name, stage: s.stage, liveHost: s.liveHost, repoUrl: s.repoUrl })) }),
    adminReport: (rid) => { const r = reports.find((x) => x.id === rid); return r ? delay(structuredClone(r)) : err(404, 'report not found'); },
    adminPublishReport: (rid) => {
      const r = reports.find((x) => x.id === rid);
      if (!r) return err(404, 'report not found');
      r.status = 'published'; r.publishedAt ??= new Date().toISOString();
      const emailed = !r.emailedAt && r.reportEmails.length > 0;
      if (emailed) { r.emailedAt = new Date().toISOString(); outbox.unshift(msg(r.slug, 'monthly_note', 'draft', 0, `Your ${new Date().toLocaleString('en-US', { month: 'long' })} website update`, 'Hi,\n\nYour report is ready.\n\nKC')); }
      return delay({ ok: true as const, emailed });
    },
    adminUnpublishReport: (rid) => { const r = reports.find((x) => x.id === rid); if (!r) return err(404, 'report not found'); r.status = 'draft'; return delay({ ok: true as const }); },
    adminRemoveLogin: (orgId, userId) => {
      const list = users[orgId] ?? [];
      const u = list.find((x) => x.id === userId);
      if (!u) return err(404, 'login not found in this workspace');
      if (u.role === 'owner' && !list.some((x) => x.id !== userId && x.role === 'owner')) return err(409, 'this is the workspace owner: invite another owner first, or the client loses their dashboard');
      users[orgId] = list.filter((x) => x.id !== userId);
      return delay({ ok: true as const, removed: u.email, loginsLeft: users[orgId]!.length });
    },
    adminInvites: (slug) => delay(structuredClone(invites[slug] ?? [])),
    adminInvite: (slug, email, businessName) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      if (s.stage === 'offboarded') return err(409, 'site is offboarded');
      const iid = uid();
      for (const i of invites[slug] ?? []) if (i.email === email.toLowerCase() && !i.acceptedAt && !i.revokedAt) i.revokedAt = new Date().toISOString();
      (invites[slug] ??= []).unshift({ id: iid, email: email.toLowerCase(), businessName: businessName ?? s.name, createdBy: 'kc@yourproject.ai', createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 14 * DAY).toISOString(), acceptedAt: null, revokedAt: null });
      return delay({ id: iid, url: `https://app.yourproject.ai/invite/mock-${iid.slice(-6)}`, expiresAt: new Date(Date.now() + 14 * DAY).toISOString() });
    },
    adminRevokeInvite: (iid) => {
      const i = Object.values(invites).flat().find((x) => x.id === iid);
      const revoked = !!i && !i.acceptedAt && !i.revokedAt;
      if (i && revoked) i.revokedAt = new Date().toISOString();
      return delay({ ok: true as const, revoked });
    },
    adminLifecycle: (status) => delay(structuredClone(tasks.filter((t) => !status || t.status === status))),
    adminCancelTask: (tid) => {
      const t = tasks.find((x) => x.id === tid);
      if (!t || t.status !== 'open') return err(409, 'only open tasks can be canceled');
      t.status = 'canceled'; t.doneAt = new Date().toISOString();
      return delay({ ...t });
    },
    adminRetryLifecycleTask: (tid) => {
      const t = tasks.find((x) => x.id === tid);
      if (!t || t.status !== 'failed') return err(409, `task is ${t?.status ?? 'missing'}, not failed`);
      t.status = 'open'; t.doneAt = null;
      return delay({ ...t });
    },
    adminAttachTask: (tid, slug) => {
      const t = tasks.find((x) => x.id === tid);
      const s = mustSite(slug);
      if (!t || t.kind !== 'prepare' || t.siteId) return err(409, 'only an unmatched prepare can be attached');
      if (!s) return notFound(slug);
      t.siteId = s.id; t.slug = s.slug; t.siteName = s.name; t.status = 'done'; t.doneAt = new Date().toISOString();
      if (s.stage === 'demo') move(s, 'sold');
      return delay({ ok: true });
    },
    adminSiteOff: () => err(409, 'site switching is off (SITE_OFF_ENABLED)'),
    adminSiteOn: () => err(409, 'site switching is off (SITE_OFF_ENABLED)'),
    adminOffboard: (slug) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      if (!['live', 'paused', 'legacy', 'offboarding'].includes(s.stage)) return err(409, `site is ${s.stage}`);
      const t = task(s.slug, 'offboard', 'open', 0, { after: 'zone_moved', byAdmin: true });
      tasks.unshift(t, task(s.slug, 'site_off', 'open', 0, { reason: 'admin', dueAt: new Date().toISOString() }));
      move(s, 'offboarding');
      if (bill[s.orgId]) bill[s.orgId]!.subStatus = 'canceled';
      log(s, 'lifecycle.offboard', { taskId: t.id });
      return delay({ ...t });
    },
    adminOutbox: (status) => delay(structuredClone(outbox.filter((m) => !status || m.status === status))),
    adminEditOutbox: (oid, p) => {
      const m = outbox.find((x) => x.id === oid);
      if (!m || !['draft', 'failed'].includes(m.status)) return err(409, 'only drafts and failed messages can be edited');
      if (p.subject !== undefined) m.subject = p.subject;
      if (p.body !== undefined) m.body = p.body;
      return delay({ ...m });
    },
    adminApproveOutbox: (oid) => {
      const m = outbox.find((x) => x.id === oid);
      if (!m) return err(404, 'message not found');
      if (!['draft', 'failed'].includes(m.status)) return err(409, `message is ${m.status}`);
      m.status = 'queued'; m.error = null;
      return delay({ ...m });
    },
    adminRetryOutbox: (oid) => {
      const m = outbox.find((x) => x.id === oid);
      if (!m || m.status !== 'failed') return err(409, `message is ${m?.status ?? 'missing'}`);
      m.status = 'queued'; m.error = null;
      return delay({ ...m });
    },
    adminCancelOutbox: (oid) => {
      const m = outbox.find((x) => x.id === oid);
      if (!m || !['draft', 'queued', 'failed'].includes(m.status)) return err(409, 'already sent or canceled');
      m.status = 'canceled';
      return delay({ ...m });
    },
    adminMonitorToggle: (slug, enabled, host) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      const h = host ?? mon[s.id]?.host ?? s.liveHost ?? '';
      if (enabled && !h) return err(400, 'no host: pass "host" or set the site liveHost first');
      mon[s.id] = { ...(mon[s.id] ?? monFor(s, 'unknown')), enabled, host: h };
      log(s, enabled ? 'monitors.enable' : 'monitors.disable', { host: h });
      return delay({ ...mon[s.id]! });
    },
    adminMonitorCheck: (slug) => {
      const s = mustSite(slug);
      const m = s ? mon[s.id] : undefined;
      if (!s || !m) return err(404, 'monitoring is not set up for this site');
      m.lastCheckAt = new Date().toISOString();
      if (m.status !== 'down') { m.status = 'up'; m.lastOkAt = m.lastCheckAt; }
      return delay({ ...m }, 700);
    },
    adminRunUpdate: (slug, postReport = false) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      if (s.stage !== 'live' && s.stage !== 'legacy') return err(409, `monthly updates run for live sites (this one is ${s.stage})`);
      if (runs.some((r) => r.siteId === s.id && r.reason === 'monthly' && !['live', 'held', 'failed', 'skipped'].includes(r.status))) return err(409, 'a monthly run is already open for this site');
      const r = run(s.slug, 'queued', 0, { postReport, requestedBy: 'kc:kc@yourproject.ai', costUsd: '0', hasFindings: false, startedAt: null, message: null });
      runs.unshift(r);
      return delay({ ...r });
    },
    adminUpdateRuns: (limit) => delay(structuredClone(runs.slice(0, limit ?? 100))),
    adminUpdateDue: () => delay({ dispatchEnabled: false, items: sites.filter((s) => s.stage === 'live' && s.nextRunAt && Date.parse(s.nextRunAt) <= Date.now())
      .map((s) => ({ slug: s.slug, name: s.name, nextRunAt: s.nextRunAt, liveAt: s.liveAt, runDay: s.runDay, kcReviewRunsLeft: s.kcReviewRunsLeft })) }),
    adminUpdateSpend: (month = ym()) => {
      const rows = runs.filter((r) => r.createdAt.slice(0, 7) === month);
      const per = new Map<string, { runs: number; monthly: number; request: number; total: number; max: number }>();
      for (const r of rows) {
        const e = per.get(r.slug!) ?? { runs: 0, monthly: 0, request: 0, total: 0, max: 0 };
        const c = Number(r.costUsd) || 0; e.runs++; e[r.reason]++; e.total += c; e.max = Math.max(e.max, c); per.set(r.slug!, e);
      }
      const total = rows.reduce((n, r) => n + (Number(r.costUsd) || 0), 0);
      const out: UpdateSpend = { month, runs: rows.length, totalUsd: total.toFixed(2), avgPerSiteUsd: per.size ? (total / per.size).toFixed(2) : '0.00',
        sites: [...per].map(([slug, e]) => ({ slug, runs: e.runs, monthlyRuns: e.monthly, requestRuns: e.request, totalUsd: e.total.toFixed(2), avgUsd: (e.total / e.runs).toFixed(2), maxUsd: e.max.toFixed(2) }))
          .sort((a, b) => Number(b.totalUsd) - Number(a.totalUsd)) };
      return delay(out);
    },
    adminReleaseRun: (rid) => {
      const r = runs.find((x) => x.id === rid);
      if (!r || r.status !== 'review') return err(409, `run is ${r?.status ?? 'missing'}, not waiting for review`);
      r.status = 'awaiting_approval'; r.message = 'released by admin:kc@yourproject.ai';
      const p = proposals.find((x) => x.id === r.proposalId);
      if (p) { p.status = p.items.some((i) => i.status === 'pending') ? 'published' : 'decided'; p.releasedAt = new Date().toISOString(); }
      const rep = reports.find((x) => x.id === r.reportId);
      if (rep) { rep.status = 'published'; rep.publishedAt = new Date().toISOString(); }
      const s = sites.find((x) => x.id === r.siteId);
      if (s) s.kcReviewRunsLeft = Math.max(0, s.kcReviewRunsLeft - 1);
      return delay({ ok: true as const, status: 'awaiting_approval', proposalStatus: p?.status ?? null, reportEmailed: rep ? true : null });
    },
    adminRetryRun: (rid) => {
      const r = runs.find((x) => x.id === rid);
      if (!r || r.status !== 'held') return err(409, `run is ${r?.status ?? 'missing'}, not held`);
      if (r.reason !== 'monthly') return err(409, 'a held request run is not retried: create a new request for the client');
      r.status = 'queued'; r.error = null; r.message = 'retried by admin:kc@yourproject.ai';
      return delay({ ok: true as const, status: 'queued' });
    },
    adminCancelRun: (rid) => {
      const r = runs.find((x) => x.id === rid);
      if (!r || ['live', 'held', 'failed', 'skipped'].includes(r.status)) return err(409, `run is already ${r?.status ?? 'missing'}`);
      r.status = 'held'; r.message = 'cancelled by admin:kc@yourproject.ai';
      return delay({ ok: true });
    },
    adminProposalsReview: () => delay(proposals.filter((p) => p.status === 'review').map((p) => {
      const s = sites.find((x) => x.id === p.siteId)!;
      const n = (c: string) => p.items.filter((i) => i.class === c).length;
      return { id: p.id, slug: s.slug, siteName: s.name, period: p.period, previewUrl: p.previewUrl, updateRunId: p.updateRunId, createdAt: p.createdAt, green: n('green'), yellow: n('yellow'), red: n('red') };
    })),
    adminProposal: (pid) => { const p = proposals.find((x) => x.id === pid); return p ? delay(structuredClone(p)) : err(404, 'proposal not found'); },
    adminDecideOnBehalf: (pid, itemId, decision) => {
      const p = proposals.find((x) => x.id === pid);
      const i = p?.items.find((x) => x.id === itemId);
      if (!p || !i) return err(404, 'proposal not found');
      if (p.status === 'review') return err(409, 'still waiting for your review: release it first');
      if (i.status !== 'pending') return err(409, 'already answered or not an open idea');
      i.status = decision === 'approve' ? 'approved' : 'declined'; i.decidedBy = 'kc:kc@yourproject.ai'; i.decidedAt = new Date().toISOString();
      if (!p.items.some((x) => x.status === 'pending')) p.status = 'decided';
      return delay({ ok: true as const, proposalStatus: p.status });
    },
    adminCreateRequest: (slug, b) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      if (s.stage !== 'live' && s.stage !== 'legacy') return err(409, 'changes open up once the site is live');
      const busy = (requests[slug] ?? []).some((r) => ['queued', 'working', 'preview_ready', 'merging'].includes(r.status));
      const row: AdminRequestRow = { id: uid(), title: b.title, status: busy ? 'waiting' : 'queued', kind: 'change', source: b.source, createdBy: 'kc:kc@yourproject.ai',
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      (requests[slug] ??= []).unshift(row);
      return delay({ id: row.id, status: row.status });
    },
    adminUploadSas: (slug, filename) => delay({ uploadUrl: `https://mock.blob.test/uploads/${slug}/${encodeURIComponent(filename)}?sig=mock`, blobUrl: `https://mock.blob.test/uploads/${slug}/${encodeURIComponent(filename)}` }),
    adminReleasePublishLock: (slug) => {
      const s = mustSite(slug);
      if (!s) return notFound(slug);
      const holder = s.publishRef; s.publishRef = null; s.publishSince = null;
      if (holder) log(s, 'site.publish_lock.admin_release', { ref: holder });
      return delay({ released: !!holder, holder });
    },
  };
}
